'use client';

import Image from 'next/image';
import { useCallback, useState } from 'react';

type TarotCard = {
  name: string;
  keywords: string;
  description: string;
  image: string;
};

export default function TarotClient({ card }: { card: TarotCard }) {
  const [isFlipped, setIsFlipped] = useState(false);

  const handleToggle = useCallback(() => {
    setIsFlipped((prev) => !prev);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggle();
      }
    },
    [handleToggle],
  );

  return (
    <>
      <style jsx>{`
        .scene {
          width: 280px;
          height: 480px;
          perspective: 1000px;
          cursor: pointer;
        }
        @media (max-width: 640px) {
          .scene {
            width: 240px;
            height: 410px;
          }
        }
        .card {
          width: 100%;
          height: 100%;
          position: relative;
          transition: transform 0.8s;
          transform-style: preserve-3d;
        }
        .card.is-flipped {
          transform: rotateY(180deg);
        }
        .card__face {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.4);
        }
        .card__face--front {
          background-color: #1a202c;
        }
        .card__face--back {
          background-color: #2d3748;
          transform: rotateY(180deg);
          color: white;
          padding: 24px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .reveal {
          animation: reveal 500ms ease-out;
        }
        @keyframes reveal {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .card {
            transition: none;
          }
          .reveal {
            animation: none;
          }
        }
      `}</style>

      <div className="bg-gradient-to-b from-indigo-900 to-black rounded-2xl text-white p-4 sm:p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 text-main">Карта Дня</h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mx-auto">
            Нажмите на карту, чтобы узнать свой прогноз на сегодня.
          </p>
        </div>

        <div className="flex flex-col items-center">
          <div
            className="scene"
            onClick={handleToggle}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="button"
            aria-label="Перевернуть карту"
            aria-pressed={isFlipped}
          >
            <div className={`card ${isFlipped ? 'is-flipped' : ''}`}>
              <div className="card__face card__face--front flex items-center justify-center p-6 border-4 border-yellow-400/50">
                <Image
                  src="/horoscope/tarot/tarot-card-back.svg"
                  alt="Рубашка карты Таро"
                  width={280}
                  height={480}
                  priority
                  sizes="(max-width: 640px) 240px, 280px"
                  className="object-cover rounded-lg"
                />
              </div>

              <div className="card__face card__face--back">
                <Image
                  src={card.image}
                  alt={card.name}
                  width={200}
                  height={300}
                  loading="lazy"
                  sizes="200px"
                  className="rounded-md object-cover"
                />
                <h2 className="text-xl sm:text-2xl font-bold mt-4 text-yellow-400">{card.name}</h2>
                <p className="text-xs sm:text-sm text-center text-gray-300 mt-2">{card.keywords}</p>
              </div>
            </div>
          </div>

          {isFlipped && (
            <div className="mt-10 max-w-2xl mx-auto reveal" role="region" aria-live="polite">
              <h3 className="text-2xl sm:text-3xl font-bold text-center mb-4 text-main">
                Ваша карта: {card.name}
              </h3>
              <p className="text-lg sm:text-xl text-gray-200 leading-relaxed whitespace-pre-line">
                {card.description}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
